import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import type { FilterValues } from "@/lib/filters";
import { tonePillClass } from "@/lib/tone";

import type { CampaignRow, Tail, Verdict } from "../api";
import {
  UNMAPPED_PRODUCT_LABEL,
  facetOptions,
  filterCampaignRows,
  marketplaceCodeLabel,
  sortCampaignRows,
  verdictTone,
  type CampaignFilters,
  type SortDir,
  type SortKey,
} from "../campaigns";
import { formatCount, formatGbp, formatRatio } from "../format";

const VERDICT_CHIPS: Verdict[] = ["scale", "hold", "cut", "insufficient-data"];

/**
 * The campaign verdicts table - sortable headers, verdict chips, five URL
 * facets, "Losing money only", a Coverage % info popover, and a tail row
 * summarising campaigns beyond the ranked set (W6).
 */
export function CampaignTable({
  campaigns,
  tail,
  region,
  values,
  onChange,
}: {
  campaigns: CampaignRow[];
  tail: Tail;
  region: string;
  values: FilterValues;
  onChange: (next: FilterValues) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("spend_gbp");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [showCoverageInfo, setShowCoverageInfo] = useState(false);

  const facets = facetOptions(campaigns);
  const filters = filtersFromValues(values);
  const activeVerdicts = verdictsFromValues(values);
  const filtered = filterCampaignRows(campaigns, filters);
  const rows = sortCampaignRows(filtered, sortKey, sortDir);
  const hasFilters =
    filters.product !== null ||
    filters.variant !== null ||
    filters.marketplace !== null ||
    filters.type !== null ||
    filters.query.trim() !== "" ||
    filters.losingOnly ||
    activeVerdicts.size > 0;

  const variantOptions = filters.product ? (facets.variantsByProduct[filters.product] ?? []) : [];

  const filterSpecs: FilterSpec[] = [
    {
      name: "product",
      label: "Product",
      kind: "choice",
      anyLabel: "All products",
      options: facets.products.map((p) => ({ value: p, label: p })),
    },
    {
      name: "variant",
      label: "Variant",
      kind: "choice",
      anyLabel: "All variants",
      options: variantOptions.map((v) => ({ value: v, label: v })),
    },
    {
      name: "marketplace",
      label: "Marketplace",
      kind: "choice",
      anyLabel: "All",
      options: facets.marketplaces.map((m) => ({ value: m, label: marketplaceCodeLabel(m) })),
    },
    {
      name: "type",
      label: "Type",
      kind: "choice",
      anyLabel: "All",
      options: facets.types.map((t) => ({ value: t, label: t })),
    },
    { name: "q", label: "Search", kind: "text", anyLabel: "campaign name" },
  ];

  function toggleVerdict(verdict: Verdict) {
    const next = new Set(activeVerdicts);
    if (next.has(verdict)) next.delete(verdict);
    else next.add(verdict);
    onChange({ verdicts: next.size > 0 ? [...next] : null });
  }

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function sortIcon(key: SortKey) {
    if (key !== sortKey) return null;
    return sortDir === "asc" ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />;
  }

  function sortHeaderClass(key: SortKey) {
    return `inline-flex items-center gap-1 ${key === sortKey ? "font-semibold text-ks-purple" : ""}`;
  }

  const columns: ColumnDef<CampaignRow, unknown>[] = [
    {
      id: "product",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("product")}
          className={sortHeaderClass("product")}
        >
          Product / Variant
          {sortIcon("product")}
        </button>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-ks-ink">
            {row.original.product ?? <span className="text-muted-foreground">{UNMAPPED_PRODUCT_LABEL}</span>}
            {row.original.variant_count > 1 && (
              <span className="ml-1.5 rounded-full border border-border px-1.5 py-0 text-[11px] text-muted-foreground">
                +{row.original.variant_count - 1}
              </span>
            )}
          </span>
          {row.original.variant && (
            <span className="text-xs text-muted-foreground">{row.original.variant}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "name",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("name")}
          className={sortHeaderClass("name")}
        >
          Campaign
          {sortIcon("name")}
        </button>
      ),
      cell: ({ row }) => <span className="text-ks-ink">{row.original.name}</span>,
    },
    {
      accessorKey: "ad_product",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("ad_product")}
          className={sortHeaderClass("ad_product")}
        >
          Type
          {sortIcon("ad_product")}
        </button>
      ),
      cell: ({ row }) => <span>{row.original.ad_product}</span>,
    },
    {
      accessorKey: "marketplace",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("marketplace")}
          className={sortHeaderClass("marketplace")}
        >
          Marketplace
          {sortIcon("marketplace")}
        </button>
      ),
      cell: ({ row }) => <span>{marketplaceCodeLabel(row.original.marketplace)}</span>,
    },
    {
      accessorKey: "spend_gbp",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("spend_gbp")}
          className={sortHeaderClass("spend_gbp")}
        >
          Spend (GBP)
          {sortIcon("spend_gbp")}
        </button>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatGbp(row.original.spend_gbp)}</span>,
    },
    {
      accessorKey: "attr_sales_gbp",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("attr_sales_gbp")}
          className={sortHeaderClass("attr_sales_gbp")}
        >
          Attr. sales (GBP)
          {sortIcon("attr_sales_gbp")}
        </button>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatGbp(row.original.attr_sales_gbp)}</span>,
    },
    {
      accessorKey: "attr_units",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("attr_units")}
          className={sortHeaderClass("attr_units")}
        >
          Attr. units
          {sortIcon("attr_units")}
        </button>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatCount(row.original.attr_units)}</span>,
    },
    {
      accessorKey: "profit_gbp",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("profit_gbp")}
          className={sortHeaderClass("profit_gbp")}
        >
          Profit (GBP)
          {sortIcon("profit_gbp")}
        </button>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatGbp(row.original.profit_gbp)}</span>,
    },
    {
      accessorKey: "profit_per_pound",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("profit_per_pound")}
          className={sortHeaderClass("profit_per_pound")}
        >
          Per GBP 1{sortIcon("profit_per_pound")}
        </button>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatRatio(row.original.profit_per_pound)}</span>,
    },
    {
      accessorKey: "coverage_pct",
      header: () => (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            data-nonmutating
            onClick={() => toggleSort("coverage_pct")}
            className={sortHeaderClass("coverage_pct")}
          >
            Coverage %{sortIcon("coverage_pct")}
          </button>
          <button
            type="button"
            data-nonmutating
            title="Share of this campaign's attributed sales priced with the product's exact contribution margin, rather than a region-average fallback (used for SB/SD or unmapped ASINs). 100% means the profit and verdict are based on real margins; lower means more of the estimate is approximated."
            onClick={() => setShowCoverageInfo((v) => !v)}
            className="text-muted-foreground"
          >
            <Info className="size-3.5" />
          </button>
        </span>
      ),
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{formatRatio(row.original.coverage_pct)}</span>,
    },
    {
      accessorKey: "verdict",
      header: () => (
        <button
          type="button"
          data-nonmutating
          onClick={() => toggleSort("verdict")}
          className={sortHeaderClass("verdict")}
        >
          Verdict
          {sortIcon("verdict")}
        </button>
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className={tonePillClass(verdictTone(row.original.verdict))}>
          {row.original.verdict}
        </Badge>
      ),
    },
  ];

  const tableRows = [...rows];
  const showTail = tail.count > 0 && !hasFilters;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Verdict
        </span>
        {VERDICT_CHIPS.map((verdict) => {
          const active = activeVerdicts.has(verdict);
          return (
            <Badge
              key={verdict}
              variant="outline"
              data-nonmutating
              aria-pressed={active}
              onClick={() => toggleVerdict(verdict)}
              className={`cursor-pointer ${active ? "bg-ks-purple-4 font-semibold text-ks-purple" : ""}`}
            >
              {verdict}
            </Badge>
          );
        })}
      </div>

      <FilterBar
        layout="inline"
        filters={filterSpecs}
        values={{ ...values, product: values.product, variant: values.variant }}
        onChange={onChange}
      />

      <label className="inline-flex w-fit items-center gap-2 text-sm text-ks-ink">
        <input
          type="checkbox"
          data-nonmutating
          className="accent-ks-purple"
          checked={filters.losingOnly}
          onChange={(e) => onChange({ losing: e.target.checked ? "1" : null })}
        />
        Losing money only
      </label>

      {showCoverageInfo && (
        <p className="text-xs text-muted-foreground">
          Coverage % is the share of this campaign's attributed sales priced with the product's exact
          contribution margin, rather than a region-average fallback.
        </p>
      )}

      {tableRows.length === 0 ? (
        <div className="flex flex-col items-start gap-2 py-4 text-sm text-muted-foreground">
          <p>No campaigns match these filters.</p>
          <button
            type="button"
            data-nonmutating
            className="text-ks-purple underline"
            onClick={() =>
              onChange({
                product: null,
                variant: null,
                marketplace: null,
                type: null,
                q: null,
                losing: null,
                verdicts: null,
              })
            }
          >
            Clear filters
          </button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={tableRows}
          page={{ pageIndex: 0, pageSize: tableRows.length || 1, rowCount: tableRows.length }}
          onPageChange={() => {}}
          caption={`Campaign verdicts for ${region}`}
        />
      )}

      {showTail ? (
        <p className="text-xs text-muted-foreground">
          + {formatCount(tail.count)} more campaigns (GBP {formatGbp(tail.spend_gbp)} spend, GBP{" "}
          {formatGbp(tail.profit_gbp)} profit)
        </p>
      ) : tail.count > 0 && hasFilters ? (
        <p className="text-xs text-muted-foreground">Tail summary hidden while filters are active</p>
      ) : null}

      <div className="grid gap-2 border-t border-border pt-4 text-xs text-muted-foreground md:grid-cols-2 xl:grid-cols-4">
        <p>
          <span className="font-medium text-ks-ink">scale</span> - a candidate to grow budget on
        </p>
        <p>
          <span className="font-medium text-ks-ink">hold</span> - keep as is
        </p>
        <p>
          <span className="font-medium text-ks-ink">cut</span> - losing contribution profit
        </p>
        <p>
          <span className="font-medium text-ks-ink">insufficient-data</span> - period still open, or spend
          below the threshold
        </p>
      </div>
    </div>
  );
}

function filtersFromValues(values: FilterValues): CampaignFilters {
  return {
    verdicts: verdictsFromValues(values),
    product: (values.product as string) || null,
    variant: (values.variant as string) || null,
    marketplace: (values.marketplace as string) || null,
    type: (values.type as string) || null,
    query: (values.q as string) || "",
    losingOnly: values.losing === "1",
  };
}

function verdictsFromValues(values: FilterValues): Set<Verdict> {
  const raw = values.verdicts;
  const list = Array.isArray(raw) ? raw : raw ? [raw as string] : [];
  return new Set(list as Verdict[]);
}
